import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CURRENCY } from '../../../../../../../../../constants/currency';
import { getDigitalDownloadPhotoPrice } from '../../../PhotoPurchasingOptions/helpers';
import Badge from '../../../../../../../../../components/global/Badge';
import Button from '../../../../../../../../../components/global/Button';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import { getAsset } from '../../../../../../../../../util/assets';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import useOpenAlert from '../../../../../../../../../hooks/useOpenAlert';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  photoPricing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    digital_downloads_price: PropTypes.shape({
      value: PropTypes.number.isRequired,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }).isRequired,
  }).isRequired,
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onDigitalDownloadToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  included: PropTypes.bool,
};

const DigitalDownloadOption = ({
  className,
  readOnly,
  photoPricing,
  cartItem,
  photo,
  onDigitalDownloadToggle,
  disabled,
  included,
}) => {
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-digital-download': true,
  });
  const price = getDigitalDownloadPhotoPrice(photo, photoPricing);

  const onAddToCart = () => {
    if (disabled) {
      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
        name: 'Hi-Res Download',
      });
      return;
    }

    if (included) {
      openAlert({
        type: 'info',
        asset: '/icon-package-3d.svg',
        message: (
          <h4 className="text-center mb-1">
            Hi-res digital download is already included in one of the print
            packages in your cart.
          </h4>
        ),
        btnLabel: 'OK',
      });
      return;
    }

    onDigitalDownloadToggle();
  };

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            <PriceDisplay
              price={price.value}
              currency={price.currency}
              trimDecimalZeros={false}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          <img
            width={60}
            src={getAsset('/digital-download-illustration.svg')}
            alt=""
          />
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">
          Hi&ndash;Res Download
        </div>
        <div className="purchasing-option-item-content mb-2">
          {!readOnly ? (
            <p className="text-sm lh-normal text-dark text-bold">
              Unlock access to hi&ndash;res digital downloads for this photo!
            </p>
          ) : (
            <p className="text-sm lh-normal text-dark text-bold">
              Unlock access to hi&ndash;res digital downloads (price per photo)!
            </p>
          )}

          {disabled && !readOnly && (
            <span className="purchasing-option-item-info-requires-package">
              Requires the purchase of a print package first.
            </span>
          )}
        </div>

        {!readOnly && (
          <Button
            btnStyle={classNames({
              primary: !cartItem,
              info: !!cartItem,
            })}
            className={classNames({
              'purchasing-options-item-btn': true,
              'btn-purchase-options-in-cart': !!cartItem,
            })}
            onClick={onAddToCart}
            data-testid="btn-digital-download"
          >
            {cartItem ? <AddedToCartLabel /> : <Fragment>Add to cart</Fragment>}
          </Button>
        )}
      </div>
    </div>
  );
};

DigitalDownloadOption.propTypes = propTypes;

export default DigitalDownloadOption;
